import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AlertModalResult } from '../components/nested-components/alert-dialog/alert-dialog.component';
import { IStaticStylesConfig } from '../skins/_primitives/IStaticStylesConfig';

export interface AlertModalConfig {
  title: string;
  message: string;
  messageClasses?: string;
  yesButtonText?: string;
  noButtonText?: string;
  Skin?: IStaticStylesConfig ;
}

export interface AlertModalState {
  isOpen: boolean;
  config: AlertModalConfig | null;
}

export enum ToastType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info'
}

export interface ToastConfig {
  id: number;
  message: string;
  type: ToastType;
  duration?: number; // in milliseconds
  dismissible?: boolean;
}

export interface ToastState {
  toasts: ToastConfig[];
}

@Injectable({
  providedIn: 'root'
})
export class AlertModalService {
  private modalState = new BehaviorSubject<AlertModalState>({
    isOpen: false,
    config: null
  });

  modalState$ = this.modalState.asObservable();

  private resultCallback: ((result: AlertModalResult) => void) | null = null;

  private toastState = new BehaviorSubject<ToastState>({
    toasts: []
  });

  toastState$ = this.toastState.asObservable();
  private toastId = 0;

  showAlert(config: AlertModalConfig): Promise<AlertModalResult> {
    this.modalState.next({
      isOpen: true,
      config
    });

    return new Promise<AlertModalResult>((resolve) => {
      this.resultCallback = resolve;
    });
  }

  hideAlert(result: AlertModalResult): void {
    this.modalState.next({
      isOpen: false,
      config: null
    });

    if (this.resultCallback) {
      this.resultCallback(result);
      this.resultCallback = null;
    }
  }

  showToast(message: string, type: ToastType, duration?: number, dismissible?: boolean): void {
    const newToast: ToastConfig = {
      id: this.toastId++,
      message,
      type,
      duration,
      dismissible
    };

    const currentToasts = this.toastState.value.toasts;
    this.toastState.next({
      toasts: [...currentToasts, newToast]
    });

    if (duration) {
      setTimeout(() => {
        this.hideToast(newToast.id);
      }, duration);
    }
  }

  hideToast(id: number): void {
    const currentToasts = this.toastState.value.toasts;
    this.toastState.next({
      toasts: currentToasts.filter(toast => toast.id !== id)
    });
  }
}
